import { getStaffVenueAssignment } from '@/utils/staffRoles';

/** Permission names from venueAssignments[].permissions.grantedPermissionNames */
export const STAFF_PERMISSION_NAMES = {
	accountingSnapshot: 'accounting_snapshot',
	cashDrawerTransactions: 'cash_drawer_transactions',
	orderCancel: 'order_cancel',
	orderLoss: 'order_loss',
	tableMove: 'table_move',
};

/**
 * UI actions gated by PIN → API permission name required to authorize.
 * Catalog flags (alwaysRequirePin / visibleWithoutPermission) override defaults when present.
 */
export const PIN_PROTECTED_ACTIONS = {
	wasteProduct: {
		id: 'wasteProduct',
		label: 'Mermar',
		permissionName: STAFF_PERMISSION_NAMES.orderLoss,
	},
	removeProduct: {
		id: 'removeProduct',
		label: 'Quitar',
		permissionName: STAFF_PERMISSION_NAMES.orderCancel,
	},
	moveProduct: {
		id: 'moveProduct',
		label: 'Mover',
		permissionName: STAFF_PERMISSION_NAMES.tableMove,
	},
};

function normalizePermissionName(name) {
	return String(name ?? '')
		.trim()
		.toLowerCase();
}

function readPermissionsBlock(assignment) {
	return assignment?.permissions ?? assignment?.permission ?? null;
}

/**
 * Flatten permissionCatalog tree into a name → node map (leaf permissions only).
 */
export function indexPermissionCatalog(catalog) {
	const byName = Object.create(null);
	const walk = (nodes) => {
		if (!Array.isArray(nodes)) return;
		for (const node of nodes) {
			if (!node || typeof node !== 'object') continue;
			const name = normalizePermissionName(node.name);
			const isGroup = Boolean(node.isGroup);
			if (name && !isGroup) {
				byName[name] = node;
			}
			if (Array.isArray(node.children) && node.children.length) {
				walk(node.children);
			}
		}
	};
	walk(catalog);
	return byName;
}

export function getCatalogPermissionByName(catalog, permissionName) {
	if (!permissionName) return null;
	const index = indexPermissionCatalog(catalog);
	return index[normalizePermissionName(permissionName)] ?? null;
}

/**
 * Catalog flags for a UI action.
 * - alwaysRequirePin: true  → pedir PIN solo si la sesión NO tiene el permiso
 * - alwaysRequirePin: false → no pedir PIN nunca para esta acción
 * - visibleWithoutPermission: true → mostrar botón aunque no tengas grant
 *
 * Si no hay entry en el catálogo, por seguridad tratamos la acción como PIN-protegida.
 */
export function getActionCatalogFlags(actionId, catalog) {
	const action = PIN_PROTECTED_ACTIONS[actionId];
	const defaults = { alwaysRequirePin: false, visibleWithoutPermission: true, permissionName: null };
	if (!action) {
		return defaults;
	}
	const entry = getCatalogPermissionByName(catalog, action.permissionName);
	if (!entry) {
		return {
			permissionName: action.permissionName,
			alwaysRequirePin: true,
			visibleWithoutPermission: true,
		};
	}
	return {
		permissionName: action.permissionName,
		alwaysRequirePin: Boolean(entry.alwaysRequirePin ?? entry.always_require_pin ?? false),
		visibleWithoutPermission: Boolean(
			entry.visibleWithoutPermission ?? entry.visible_without_permission ?? true,
		),
	};
}

/**
 * Snapshot of permissions for this staff on the given venue.
 * @returns {{ isFullAccess: boolean, grantedPermissionNames: string[] }}
 */
export function resolveStaffPermissionSnapshot(staff, venueId) {
	const empty = { isFullAccess: false, grantedPermissionNames: [] };
	if (!staff) {
		return empty;
	}

	const assignment = getStaffVenueAssignment(staff, venueId);
	const permissions = readPermissionsBlock(assignment);
	if (!permissions) {
		return empty;
	}

	const isFullAccess = Boolean(permissions.isFullAccess ?? permissions.is_full_access);
	const rawNames = permissions.grantedPermissionNames ?? permissions.granted_permission_names ?? [];
	const grantedPermissionNames = (Array.isArray(rawNames) ? rawNames : [])
		.map(normalizePermissionName)
		.filter(Boolean);

	return { isFullAccess, grantedPermissionNames };
}

/**
 * grantedPermissionNames for this staff on the given venue (empty if none).
 */
export function getStaffGrantedPermissionNames(staff, venueId) {
	return resolveStaffPermissionSnapshot(staff, venueId).grantedPermissionNames;
}

/**
 * ¿El staff tiene el permission name del API en su assignment del venue?
 */
export function staffHasGrantedPermission(staff, permissionName, venueId) {
	if (!staff || !permissionName) {
		return false;
	}
	const { isFullAccess, grantedPermissionNames } = resolveStaffPermissionSnapshot(staff, venueId);
	if (isFullAccess) {
		return true;
	}
	return grantedPermissionNames.includes(normalizePermissionName(permissionName));
}

/**
 * Check against an already-resolved permission snapshot (from login).
 */
export function snapshotAllowsPermission(snapshot, permissionName) {
	if (!permissionName) {
		return false;
	}
	if (!snapshot) {
		return false;
	}
	if (snapshot.isFullAccess) {
		return true;
	}
	const names = snapshot.grantedPermissionNames ?? [];
	return names.includes(normalizePermissionName(permissionName));
}

/**
 * Whether current session may skip the PIN modal for this action.
 *
 * alwaysRequirePin === false → nunca pedir PIN
 * alwaysRequirePin === true  → pedir PIN solo si la sesión no tiene el grant
 *   (si Lucho ya tiene order_loss, no sale el modal)
 */
export function canSkipPinForAction(actionId, { catalog, snapshot, staff, venueId } = {}) {
	const flags = getActionCatalogFlags(actionId, catalog);
	if (!flags.alwaysRequirePin) {
		return true;
	}
	if (snapshotAllowsPermission(snapshot, flags.permissionName)) {
		return true;
	}
	return staffHasGrantedPermission(staff, flags.permissionName, venueId);
}

/**
 * Whether the action button should be shown.
 * visibleWithoutPermission=false → only if session has the grant.
 */
export function isProtectedActionVisible(actionId, { catalog, snapshot, staff, venueId } = {}) {
	if (!PIN_PROTECTED_ACTIONS[actionId]) {
		return true;
	}
	const flags = getActionCatalogFlags(actionId, catalog);
	if (flags.visibleWithoutPermission) {
		return true;
	}
	if (snapshotAllowsPermission(snapshot, flags.permissionName)) {
		return true;
	}
	return staffHasGrantedPermission(staff, flags.permissionName, venueId);
}

/** @deprecated prefer canSkipPinForAction — kept for call sites that pass staff/venue only */
export function hasStaffPermission(staff, actionId, venueId) {
	return canSkipPinForAction(actionId, { staff, venueId, catalog: null, snapshot: null });
}

/**
 * ¿El PIN introducido en el overlay autoriza la acción?
 */
export function canAuthorizeActionWithPin(staff, actionId, venueId) {
	const action = PIN_PROTECTED_ACTIONS[actionId];
	if (!action) {
		return true;
	}
	return staffHasGrantedPermission(staff, action.permissionName, venueId);
}

export function findStaffByPin(staffList, pin) {
	const needle = String(pin ?? '');
	return (staffList || []).find((s) => String(s?.pin ?? '') === needle) ?? null;
}
